import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Switch from "@mui/material/Switch";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import { api } from "../../hooks/apiClient";
import { useStore } from "../../context/StoreContext";
import SelectCourier from "../../components/forms/SelectCourier";
import PinCodeDialog from "../../components/dialogs/PinCodeDialog";
import SelectStoreCard from "./components/SelectStoreCard";
import PaymentSchedulePicker from "./components/PaymentSchedulePicker";
import CourierChips from "./components/CourierChips";
import { mockCouriers } from "../../components/dataSamples/couriers";
/* Scoped styles (sd- prefix). Bootstrap and MUI stay as they are. */
const css = `
.sd-page{max-width:1180px;margin:0 auto;text-align:left}
.sd-back{font-size:14px;color:#4a5a54;text-decoration:none}
.sd-back:hover{color:#0b5d44}
.sd-head{display:flex;flex-wrap:wrap;align-items:flex-start;justify-content:space-between;gap:12px;margin:8px 0 20px}
.sd-title{font-size:24px;font-weight:600;letter-spacing:-.01em;margin:0;color:#1d2a26}
.sd-sub{color:#5b6b65;font-size:14px;margin:2px 0 0;word-break:break-all}
.sd-chips{display:flex;gap:8px;flex-wrap:wrap}
.sd-chip{display:inline-flex;align-items:center;gap:6px;border-radius:999px;padding:4px 12px;font-size:13px;font-weight:500}
.sd-chip::before{content:"";width:7px;height:7px;border-radius:50%;background:currentColor}
.sd-ok{background:#e3f5ec;color:#14704a}
.sd-warn{background:#fff3d6;color:#7a5200}
.sd-off{background:#eceff0;color:#4a5a54}
.sd-card{background:#fff;border:1px solid #dde3e0;border-radius:12px;padding:20px;margin-bottom:16px}
.sd-card h2{font-size:15px;font-weight:600;margin:0 0 4px;color:#1d2a26}
.sd-hint{font-size:13px;color:#5b6b65;margin:0 0 14px}
.sd-dl{display:grid;grid-template-columns:140px 1fr;gap:10px 16px;margin:0;font-size:14px}
.sd-dl dt{color:#5b6b65;font-weight:400}
.sd-dl dd{margin:0;color:#1d2a26;word-break:break-all}
.sd-row{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:14px 0}
.sd-row + .sd-row{border-top:1px solid #edf1ef}
.sd-row strong{display:block;font-size:14px;font-weight:600;color:#1d2a26}
.sd-row p{margin:0;font-size:13px;color:#5b6b65}
.sd-danger{border-color:#f0cfcb}
.sd-danger h2{color:#8f2418}
.sd-bar{display:flex;align-items:center;justify-content:space-between;gap:8px;background:#f2f4f3;border-radius:8px;padding:8px 12px;margin-top:12px;font-size:13px}
.sd-empty{text-align:center;color:#5b6b65;padding:18px 0;font-size:14px;margin:0}
.sd-label{display:block;font-size:13px;font-weight:500;color:#31403b;margin-bottom:4px}
@media (max-width:575px){.sd-dl{grid-template-columns:1fr}.sd-row{flex-wrap:wrap}}
`;

const msgOf = (err, fallback = "Something went wrong") => {
  const d = err?.response?.data;
  if (typeof d === "string" && d) return d;
  if (d?.message || d?.error) return d.message || d.error;
  return err?.response ? fallback : "Cannot reach the server";
};

const StoreDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedStore, updateSelectedStore } = useStore();

  const [storeData, setStoreData] = useState(null);
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(null); // "sync" | "approve" | "save" | "pay" | "webhooks" | "hook" | "pin"

  const [storeActive, setStoreActive] = useState(false);
  const [payActive, setPayActive] = useState(false);
  const [payCycle, setPayCycle] = useState("0");
  const [payTime, setPayTime] = useState(dayjs());
  const [webhooks, setWebhooks] = useState([]);
  const [webhooksLoaded, setWebhooksLoaded] = useState(false);
  const [WABILoaded, setWABILoaded] = useState(false);
  const [hookOpen, setHookOpen] = useState(false);
  const [hookTopic, setHookTopic] = useState("");
  const [hookAddress, setHookAddress] = useState("");

  const [pinOpen, setPinOpen] = useState(false);
  const [pinAction, setPinAction] = useState("sku"); // "sku" | "reset"
  const [confirm, setConfirm] = useState(null);
  const [snackbar, setSnackbar] = useState(null);

  const notify = (children, severity = "success") =>
    setSnackbar({ children, severity });

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      setLoadError(false);
      try {
        const [storeRes, courierRes] = await Promise.all([
          api.get(`stores/${id}`),
          // api
          //   .get("couriers/all/active/bystore", { params: { store_id: id } })
          //   .catch(() => ({ data: [] })),
        ]);
        const data = storeRes.data.data;
        setStoreData(data);
        setStoreActive(Boolean(data.isActive));
        setPayActive(Boolean(data.ordersPayIsActive));
        setCouriers(mockCouriers || []);
      } catch (err) {
        console.error("Error loading store:", err);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    load();
  }, [load]);

  const approved = storeData
    ? Boolean(storeData.isApproved ?? storeData.status === "Approved")
    : false;

  // Keep the saved "working store" (localStorage) in step with the real status
  useEffect(() => {
    if (!storeData || selectedStore?.id !== storeData.id) return;
    const isActive = Boolean(storeData.isActive);
    if (
      selectedStore.isActive !== isActive ||
      selectedStore.isApproved !== approved
    ) {
      updateSelectedStore({ ...selectedStore, isActive, isApproved: approved });
    }
  }, [storeData, approved, selectedStore, updateSelectedStore]);

  /* ---------- status and approval ---------- */
  const dirty = storeData ? storeActive !== Boolean(storeData.isActive) : false;

  const saveActive = async () => {
    setBusy("save");
    try {
      const res = await api.put(`stores/active/${id}`, {
        isActive: storeActive,
      });
      setStoreData((s) => ({ ...s, isActive: storeActive }));
      notify(res.data?.message || "Store updated");
    } catch (err) {
      setStoreActive(Boolean(storeData.isActive));
      notify(msgOf(err, "Could not save the change"), "error");
    } finally {
      setBusy(null);
    }
  };

  const onSaveClick = () => {
    if (storeActive) return saveActive();
    setConfirm({
      title: "Deactivate this store?",
      body: "Orders and stock will stop syncing for this store until you activate it again.",
      confirmLabel: "Deactivate",
      danger: true,
      onConfirm: saveActive,
    });
  };

  const requestApproval = async () => {
    setBusy("approve");
    try {
      await api.get(`stores/approval/${id}`);
      notify("Store updated");
      await load(true);
    } catch (err) {
      notify(msgOf(err, "Approval failed"), "error");
    } finally {
      setBusy(null);
    }
  };

  /* ---------- order pay system ---------- */
  const canSave = !!payTime && !!payCycle;
  const handlePayToggle = async (e) => {
    const checked = e.target.checked;
    if (checked && !canSave) {
      notify(
        !payTime && !(Number(payCycle) > 0)
          ? "Choose a time and a payout cycle first"
          : !payTime
            ? "Choose a payout time first"
            : "Choose a payout cycle first",
        "warning",
      );
      return;
    }

    setBusy("pay");
    try {
      const res = await api.put(`stores/orderspay/${id}`, {
        ordersPayIsActive: checked,
      });
      setPayActive(Boolean(res.data.data));
      // console.log(res);
      notify("Order pay system updated");
    } catch (err) {
      notify(
        msgOf(err.message, "Could not update the order pay system"),
        "error",
      );
    } finally {
      setBusy(null);
    }
  };

  /* ---------- product tools ---------- */
  const syncProducts = async () => {
    setBusy("sync");
    try {
      const res = await api.get(`products/shopifysync/${id}`);
      notify(typeof res.data === "string" ? res.data : "Products synced");
    } catch (err) {
      notify(msgOf(err, "Sync failed"), "error");
    } finally {
      setBusy(null);
    }
  };

  const generateSkus = async (pin) => {
    setBusy("pin");
    try {
      const res = await api.post(`products/skus/bulk/store/${id}`, {
        pin_code: pin,
      });
      notify(typeof res.data === "string" ? res.data : "SKUs generated");
    } catch (err) {
      notify(msgOf(err, "Could not generate SKUs"), "error");
    } finally {
      setBusy(null);
      setPinOpen(false);
    }
  };

  const resetProducts = async (pin) => {
    setBusy("pin");
    try {
      const res = await api.post(`products/hard/reset/store/${id}`, {
        pin_code: pin,
      });
      notify(typeof res.data === "string" ? res.data : "Products reset");
    } catch (err) {
      notify(msgOf(err, "Reset failed"), "error");
    } finally {
      setBusy(null);
      setPinOpen(false);
    }
  };

  const runPinAction = (pin) => {
    if (!pin) {
      notify("Enter your PIN", "warning");
      return;
    }
    return pinAction === "reset" ? resetProducts(pin) : generateSkus(pin);
  };

  /* ---------- webhooks ---------- */
  const loadWebhooks = async () => {
    setBusy("webhooks");
    try {
      const res = await api.get(`webhook/getwebhooks/${id}`);
      setWebhooks(Array.isArray(res.data) ? res.data : []);
      setWebhooksLoaded(true);
    } catch (err) {
      notify(msgOf(err, "Could not load webhooks"), "error");
    } finally {
      setBusy(null);
    }
  };

  const registerWebhook = async () => {
    if (!hookTopic.trim() || !hookAddress.trim()) {
      notify("Enter a webhook topic and address", "warning");
      return;
    }
    if (!/^https:\/\//i.test(hookAddress.trim())) {
      notify("The webhook address must start with https://", "warning");
      return;
    }
    setBusy("hook");
    try {
      const res = await api.post(`webhook/regwebhook/${id}`, {
        hookTopic: hookTopic.trim(),
        hookAddress: hookAddress.trim(),
      });
      notify(res.data?.message || "Webhook registered");
      setHookOpen(false);
      setHookTopic("");
      setHookAddress("");
      await loadWebhooks();
    } catch (err) {
      notify(`Could not register webhook: ${msgOf(err)}`, "error");
    } finally {
      setBusy(null);
    }
  };

  const deleteWebhook = async (webhookId) => {
    setBusy("webhooks");
    try {
      await api.post(`webhook/delwebhook/${id}`, { webhookId });
      setWebhooks((list) => list.filter((w) => w.id !== webhookId));
      notify("Webhook deleted");
    } catch (err) {
      notify(msgOf(err, "Could not delete the webhook"), "error");
    } finally {
      setBusy(null);
    }
  };

  const created = storeData?.created_at ?? storeData?.createdAt;

  /* ---------- render ---------- */
  if (loading) {
    return (
      <div className="sd-page text-center py-5">
        <CircularProgress size={28} aria-label="Loading store" />
      </div>
    );
  }

  if (loadError || !storeData) {
    return (
      <div className="sd-page">
        <style>{css}</style>
        <div className="sd-card text-center">
          <h2>We couldn't load this store</h2>
          <p className="sd-hint">Check your connection and try again.</p>
          <button
            type="button"
            className="btn btn-success btn-sm me-2"
            onClick={() => load()}
          >
            Try again
          </button>
          <Link to="/stores" className="btn btn-outline-secondary btn-sm">
            Back to stores
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="sd-page">
      <style>{css}</style>

      {!!snackbar && (
        <Snackbar
          open
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackbar(null)}
          autoHideDuration={6000}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}

      <Link to="/stores" className="sd-back">
        <i className="bi bi-arrow-left" aria-hidden="true" /> All stores
      </Link>

      <div className="sd-head">
        <div>
          <h1 className="sd-title">{storeData.public_name}</h1>
          <p className="sd-sub">{storeData.domain}</p>
        </div>
        <div className="sd-chips">
          <span
            className={`sd-chip ${id === String(selectedStore?.id) ? "sd-ok" : "sd-off"}`}
          >
            {selectedStore
              ? `Selected: ${selectedStore?.public_name}`
              : "No store selected"}
          </span>
          <span className={`sd-chip ${approved ? "sd-ok" : "sd-warn"}`}>
            {approved ? "Approved" : "Pending approval"}
          </span>
          <span
            className={`sd-chip ${storeData.isActive ? "sd-ok" : "sd-off"}`}
          >
            {storeData.isActive ? "Active" : "Not active"}
          </span>
        </div>
      </div>

      <div className="row">
        {/* ---------- main column ---------- */}
        <div className="col-lg-8">
          <section className="sd-card">
            <h2>Store details</h2>
            <p className="sd-hint">Basic information about this connection.</p>
            <dl className="sd-dl">
              <dt>Domain</dt>
              <dd>{storeData.domain}</dd>
              <dt>Store ID</dt>
              <dd>{storeData.id}</dd>
              {storeData.type && (
                <>
                  <dt>Type</dt>
                  <dd className="text-capitalize">{storeData.type}</dd>
                </>
              )}
              {created && (
                <>
                  <dt>Created</dt>
                  <dd>{dayjs(created).format("D MMM YYYY, h:mm A")}</dd>
                </>
              )}
            </dl>
          </section>

          <section className="sd-card">
            <h2>Product tools</h2>
            <p className="sd-hint">
              Keep this store's catalog in step with Shopify.
            </p>
            {approved ? (
              <>
                <div className="sd-row">
                  <div>
                    <strong>Sync products</strong>
                    <p>Pull the latest products and inventory from Shopify.</p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-success btn-sm"
                    disabled={busy === "sync"}
                    onClick={() =>
                      setConfirm({
                        title: "Sync all products?",
                        body: "This pulls every product from Shopify into this store.",
                        confirmLabel: "Sync now",
                        onConfirm: syncProducts,
                      })
                    }
                  >
                    {busy === "sync" ? "Syncing…" : "Sync now"}
                  </button>
                </div>
                <div className="sd-row">
                  <div>
                    <strong>Generate SKUs</strong>
                    <p>
                      Create SKUs for every product in bulk. Needs your PIN.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setPinAction("sku");
                      setPinOpen(true);
                    }}
                  >
                    Generate
                  </button>
                </div>
              </>
            ) : (
              <p className="sd-empty">
                Product tools unlock once this store is approved.
              </p>
            )}
          </section>

          <section className="sd-card">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
              <div>
                <h2>Webhooks</h2>
                <p className="sd-hint mb-0">
                  Endpoints Shopify calls when something changes.
                </p>
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={loadWebhooks}
                  disabled={busy === "webhooks"}
                >
                  {webhooksLoaded ? "Refresh" : "Load webhooks"}
                </button>
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={() => setHookOpen(true)}
                >
                  Register webhook
                </button>
              </div>
            </div>

            {!webhooksLoaded ? (
              <p className="sd-empty">
                Press "Load webhooks" to see what is registered.
              </p>
            ) : webhooks.length === 0 ? (
              <p className="sd-empty">No webhooks registered for this store.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Topic</th>
                      <th>Address</th>
                      <th>Created</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {webhooks.map((w) => (
                      <tr key={w.id}>
                        <td className="text-nowrap">{w.topic}</td>
                        <td className="text-break">{w.address}</td>
                        <td className="text-nowrap">
                          {w.created_at
                            ? dayjs(w.created_at).format("D MMM YYYY")
                            : ""}
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            disabled={busy === "webhooks"}
                            onClick={() =>
                              setConfirm({
                                title: "Delete this webhook?",
                                body: `Shopify will stop sending "${w.topic}" events to this address.`,
                                confirmLabel: "Delete",
                                danger: true,
                                onConfirm: () => deleteWebhook(w.id),
                              })
                            }
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          <section className="sd-card">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
              <div>
                <h2>Whatsapp Connection</h2>
                <p className="sd-hint mb-0">
                  Notification of Shopify orders when customer want.
                </p>
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => alert("option not availble")}
                  disabled={busy === "whatsapp"}
                >
                  {WABILoaded ? "Refresh" : "Load WhatsApp Status"}
                </button>
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={() => navigate("/connect-whatsapp")}
                >
                  Connect Whatsapp
                </button>
              </div>
            </div>
            <p className="sd-empty">
              Press "Load WhatsApp Status" to see what is registered.
            </p>
          </section>
          {approved && (
            <section className="sd-card sd-danger">
              <h2>Danger zone</h2>
              <div className="sd-row">
                <div>
                  <strong>Reset all products</strong>
                  <p>
                    Deletes every product stored for this store. This cannot be
                    undone.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() =>
                    setConfirm({
                      title: "Reset all products?",
                      body: "You will be asked for your PIN next. All products for this store will be removed.",
                      confirmLabel: "Continue",
                      danger: true,
                      onConfirm: () => {
                        setPinAction("reset");
                        setPinOpen(true);
                      },
                    })
                  }
                >
                  Reset products
                </button>
              </div>
            </section>
          )}
        </div>

        {/* ---------- side column ---------- */}
        <div className="col-lg-4">
          <section className="sd-card">
            <h2>Working store</h2>
            <p className="sd-hint">
              Choose which store the rest of the CRM shows.
            </p>
            <SelectStoreCard
              store={{ ...storeData, isApproved: approved }}
              storeType="source"
            />
          </section>

          <section className="sd-card">
            <h2>Status and access</h2>
            <div className="sd-row">
              <div>
                <strong>Approval</strong>
                <p>
                  {approved
                    ? "This store is approved."
                    : "Waiting for approval."}
                </p>
              </div>
              {!approved && storeData.status === "Pending" && (
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm"
                  onClick={requestApproval}
                  disabled={busy === "approve"}
                >
                  {busy === "approve" ? "Working…" : "Approve and activate"}
                </button>
              )}
            </div>
            <div className="sd-row">
              <div>
                <strong>Store is active</strong>
                <p>Inactive stores don't sync orders or stock.</p>
              </div>
              <Switch
                color="success"
                checked={storeActive}
                onChange={(e) => setStoreActive(e.target.checked)}
                inputProps={{ "aria-label": "Store is active" }}
              />
            </div>
            {dirty && (
              <div className="sd-bar" role="status">
                <span>Unsaved change</span>
                <span className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-secondary p-0"
                    onClick={() => setStoreActive(Boolean(storeData.isActive))}
                  >
                    Undo
                  </button>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={onSaveClick}
                    disabled={busy === "save"}
                  >
                    {busy === "save" ? "Saving…" : "Save"}
                  </button>
                </span>
              </div>
            )}
          </section>

          <section className="sd-card">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="mb-1">Order pay system</h2>
                <p className="sd-hint mb-0">
                  Automatically collect order payments on a schedule.
                </p>
              </div>
              <Switch
                color="success"
                checked={payActive}
                onChange={handlePayToggle}
                disabled={busy === "pay"}
                inputProps={{ "aria-label": "Order pay system enabled" }}
              />
            </div>

            <PaymentSchedulePicker
              time={payTime}
              onTimeChange={setPayTime}
              cycle={payCycle}
              onCycleChange={setPayCycle}
              disabled={busy === "pay"}
              idPrefix="order-pay"
            />
          </section>

          <section className="sd-card">
            <h2>Couriers</h2>
            <CourierChips couriers={couriers} />
          </section>

          <section className="sd-card">
            <h2>Sales</h2>
            <p className="sd-hint">
              See units sold and net sales for this store.
            </p>
            <Link
              to={`/salereports/${id}`}
              className="btn btn-outline-secondary btn-sm"
            >
              View sales report
            </Link>
          </section>
        </div>
      </div>

      {/* ---------- dialogs ---------- */}
      <PinCodeDialog
        openDialog={pinOpen}
        setOpenDialog={setPinOpen}
        actionFunction={runPinAction}
        dialogTitle={pinAction === "reset" ? "Confirm reset" : "PIN code"}
        dialogContent={
          pinAction === "reset"
            ? "Enter your PIN to delete all products in this store. Numbers only."
            : "Enter your PIN to generate SKUs for all products. Numbers only."
        }
      />

      <Dialog
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{confirm?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirm?.body}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirm?.danger ? "error" : "success"}
            onClick={() => {
              const run = confirm.onConfirm;
              setConfirm(null);
              run();
            }}
          >
            {confirm?.confirmLabel || "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={hookOpen}
        onClose={() => setHookOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Register a webhook</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Topic"
            placeholder="orders/create"
            value={hookTopic}
            onChange={(e) => setHookTopic(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Address"
            placeholder="https://api.vnoirs.com/..."
            helperText="Must be a public https:// address"
            value={hookAddress}
            onChange={(e) => setHookAddress(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setHookOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={registerWebhook}
            disabled={busy === "hook"}
          >
            {busy === "hook" ? "Registering…" : "Register"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default StoreDetail;
